import { create } from "zustand";
import { DEFAULT_VIEW, normalizeView, type ViewSettings } from "./viewSettings.ts";

type Views = Record<string, ViewSettings>;
type StorageAccess = Pick<Storage, "getItem" | "setItem">;
type Adapter = {
  namespace: string;
  storage: () => StorageAccess;
  load: (signal: AbortSignal) => Promise<{ view_key: string; settings: unknown }[]>;
  save: (key: string, settings: ViewSettings, signal: AbortSignal) => Promise<unknown>;
};
type State = {
  userId: string | null;
  views: Views;
  pending: Views;
  loaded: boolean;
  hydrate: (userId: string) => Promise<void>;
  reset: () => void;
  get: (key: string) => ViewSettings;
  set: (key: string, patch: Partial<ViewSettings>) => void;
  flush: () => Promise<void>;
};

function normalizeViews(value: unknown): Views {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  return Object.fromEntries(Object.entries(value).map(([key, settings]) => [key, normalizeView(settings)]));
}

/** One user/instance at a time. Unsynced edits survive reloads and slow reads. */
export function createViewStore(adapter: Adapter) {
  const timers = new Map<string, ReturnType<typeof setTimeout>>();
  const writes = new Map<string, Promise<void>>();
  const controllers = new Set<AbortController>();
  let generation = 0;
  let hydration: Promise<void> | undefined;
  let hydrationEdits = new Set<string>();
  const localKey = (userId: string) => `tern.views.v2:${adapter.namespace}:${userId}`;

  return create<State>((set, get) => {
    const persist = () => {
      const { userId, views, pending } = get();
      if (!userId) return;
      try { adapter.storage().setItem(localKey(userId), JSON.stringify({ views, pending })); } catch { /* Storage may be unavailable. */ }
    };
    const send = (key: string): Promise<void> => {
      clearTimeout(timers.get(key)); timers.delete(key);
      const epoch = generation;
      const write = (writes.get(key) ?? Promise.resolve()).then(async () => {
        if (epoch !== generation || !get().userId) return;
        const settings = get().pending[key];
        if (!settings) return;
        const controller = new AbortController(); controllers.add(controller);
        try {
          await adapter.save(key, settings, controller.signal);
          if (epoch === generation && get().pending[key] === settings) {
            set(state => { const pending = { ...state.pending }; delete pending[key]; return { pending }; });
            persist();
          }
        } catch { /* Keep the local edit for the next flush or sign-in. */ }
        finally { controllers.delete(controller); }
      });
      writes.set(key, write);
      void write.finally(() => { if (writes.get(key) === write) writes.delete(key); });
      return write;
    };
    const schedule = (key: string) => {
      clearTimeout(timers.get(key));
      timers.set(key, setTimeout(() => { void send(key); }, 800));
    };

    return {
      userId: null, views: {}, pending: {}, loaded: false,
      reset() {
        generation++;
        timers.forEach(clearTimeout); timers.clear();
        controllers.forEach(controller => controller.abort()); controllers.clear();
        writes.clear(); hydration = undefined; hydrationEdits = new Set();
        set({ userId: null, views: {}, pending: {}, loaded: false });
      },
      hydrate(userId) {
        if (get().userId !== userId) {
          get().reset();
          let views: Views = {}, pending: Views = {};
          try {
            const cached = JSON.parse(adapter.storage().getItem(localKey(userId)) ?? "{}");
            views = normalizeViews(cached?.views); pending = normalizeViews(cached?.pending);
          } catch { /* Fall back to the server, never another user's legacy cache. */ }
          set({ userId, views: { ...views, ...pending }, pending });
          hydrationEdits = new Set(Object.keys(pending));
        }
        if (get().loaded) return Promise.resolve();
        if (hydration) return hydration;
        const epoch = generation;
        const controller = new AbortController(); controllers.add(controller);
        const request = (async () => {
          try {
            const rows = await adapter.load(controller.signal);
            if (epoch !== generation) return;
            const server = Object.fromEntries(rows.map(row => [row.view_key, normalizeView(row.settings)]));
            // A save may finish before an older GET: keep edits made during hydration.
            set(state => ({ views: { ...server, ...Object.fromEntries([...hydrationEdits].map(key => [key, state.views[key]!])), ...state.pending } }));
            persist();
          } catch { /* Retain this user's cache when offline. */ }
          finally {
            controllers.delete(controller);
            if (epoch === generation) {
              set({ loaded: true });
              Object.keys(get().pending).forEach(schedule);
            }
          }
        })();
        hydration = request;
        return request;
      },
      get: key => get().views[key] ?? DEFAULT_VIEW,
      set(key, patch) {
        if (!get().userId) return;
        if (!get().loaded) hydrationEdits.add(key);
        const next = normalizeView({ ...get().get(key), ...patch });
        set(state => ({ views: { ...state.views, [key]: next }, pending: { ...state.pending, [key]: next } }));
        persist(); schedule(key);
      },
      async flush() { await Promise.all(Object.keys(get().pending).map(send)); },
    };
  });
}
